import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, empty, Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, map, timeout, first, switchMap } from 'rxjs/operators';
import { BaseService, IConfigService, ILoggingService, IModelEntity, IUtilsDictionary, UtilsDictionary, UtilsMisc, UtilsString, retryWithDelay, ErrorTimeout, ErrorHttp, ErrorUnknown, ErrorNoNetwork } from '@pit/core';
import { IEntityDataService, IListResult, RequestParams, StorageType } from '../interfaces/entity.data.service.interface';
import { ILocalStorageProvider } from '../interfaces/local.storage.provider.interface';

export class EntityDataService<T extends IModelEntity> extends BaseService implements IEntityDataService<T> {

  public static storageType: StorageType = StorageType.Remote;

  protected _urls: IUtilsDictionary<string> = null;
  protected http: HttpClient = null;
  protected localStorage: ILocalStorageProvider<T> = null;

  constructor(http: HttpClient, localStorage: ILocalStorageProvider<T>, configService: IConfigService, loggingService: ILoggingService) {
    super(configService, loggingService);

    this.http = http;
    this.localStorage = localStorage;

    this.loggingService.debug('EntityDataService --> constructor()');
  }

  public get apiUrl(): string {
    return this.doGetApiUrl();
  }

  public delete$(entity: T, storageType?: StorageType): Observable<any> {
    return this.doDelete$(entity, storageType);
  }

  public getAll$(storageType: StorageType = null): Observable<T[]> {
    return this.doGetAll$(storageType);
  }

  public getAllWithPaging$(startRow: number, rowsToTake?: number): Observable<IListResult<T>> {
    return this.doGetAllWithPaging$({ query: 'getallwithpaging', startRow: startRow, rowsToTake: rowsToTake });
  }

  public getById$(id: number, storageType?: StorageType): Observable<T> {
    return this.doGetById$(id, storageType);
  }

  public getByQuery$(requestParams: RequestParams): Observable<IListResult<T>> {
    return this.doGetByQuery$(requestParams);
  }

  public save$(entity: T, storageType?: StorageType): Observable<T> {
    return this.doSave$(entity, storageType);
  }

  public saveMulti$(entities: T[]): Observable<boolean> {
    return this.doSaveMulti$(entities);
  }

  public async deleteAsync(entity: T, storageType?: StorageType): Promise<any> {
    return (await this.delete$(entity, storageType).toPromise());
  }

  public async getAllAsync(storageType: StorageType = null): Promise<T[]> {
    let result: T[] = [];
    result = await this.getAll$(storageType).toPromise();
    if (UtilsMisc.isNullOrUndefinedOrEmpty(result) === true) {
      result = null;
    }

    return result;
  }

  public async getAllWithPagingAsync(startRow: number, rowsToTake?: number): Promise<IListResult<T>> {
    let result: IListResult<T> = null;
    result = await this.getAllWithPaging$(startRow, rowsToTake).toPromise();
    if (UtilsMisc.isNullOrUndefinedOrEmpty(result) === true) {
      result = null;
    }

    return result;
  }

  public async getByIdAsync(id: number, storageType?: StorageType): Promise<T> {
    let result: T = await this.getById$(id, storageType).toPromise();
    if (UtilsMisc.isNullOrUndefinedOrEmpty(result) === true) {
      result = null;
    }

    return result;
  }

  public async getByQueryAsync(requestParams: RequestParams): Promise<IListResult<T>> {
    let result: IListResult<T> = await this.getByQuery$(requestParams).toPromise();
    if (UtilsMisc.isNullOrUndefinedOrEmpty(result) === true) {
      result = null;
    }

    return result;
  }

  public async saveAsync(entity: T, storageType?: StorageType): Promise<T> {
    let result: T = await this.save$(entity, storageType).toPromise();
    if (UtilsMisc.isNullOrUndefinedOrEmpty(result) === true) {
      result = null;
    }

    return result;
  }

  public async saveMultiAsync(entities: T[]): Promise<boolean> {
    return (await this.saveMulti$(entities).toPromise());
  }

  protected get urls(): IUtilsDictionary<string> {
    return this.doGetUrls();
  }

  protected doGetApiUrl(): string {
    return null;
  }

  protected doDelete$(entity: T, storageType?: StorageType): Observable<any> {
    let result$: Observable<any> = EMPTY;

    if (UtilsMisc.isNullOrUndefined(storageType) === true) {
      storageType = EntityDataService.storageType;
    }

    const entityToDelete: T = (entity.clone() as T);
    entityToDelete.removeReferences();

    switch (storageType) {
      case StorageType.Local: {
        result$ = this.doDeleteLocal$(entityToDelete);
        break;
      }
      case StorageType.Remote: {
        result$ = this.doDeleteRemote$(entityToDelete);
        break;
      }
    }

    return result$;
  }

  protected doDeleteLocal$(entityToDelete: T): Observable<any> {
    let result$: Observable<any> = EMPTY;

    if (this.localStorage !== null) {
      this.localStorage.delete$(entityToDelete);
    }
    result$ = of(entityToDelete.id);

    return result$;
  }

  protected doDeleteRemote$(entityToDelete: T): Observable<any> {
    return this.httpDelete(this.createUrlForQuery({ query: 'delete', urlParams: [entityToDelete.id.toString()] }), entityToDelete.transactionId)
      .pipe(map(value => {
        return value;
      }));
  }

  protected doGetAll$(storageType: StorageType = null): Observable<T[]> {
    let result$: Observable<T[]> = EMPTY;

    if (UtilsMisc.isNullOrUndefined(storageType) === true) {
      storageType = EntityDataService.storageType;
    }

    switch (storageType) {
      case StorageType.Local: {
        result$ = this.doGetAllLocal$();
        break;
      }
      case StorageType.Remote: {
        result$ = this.doGetAllRemote$();
        break;
      }
    }

    return result$;
  }

  protected doGetAllLocal$(): Observable<T[]> {
    let result$: Observable<T[]> = EMPTY;

    if (this.localStorage !== null) {
      result$ = this.localStorage.getAll$();
    }

    return result$;
  }

  protected doGetAllRemote$(): Observable<T[]> {
    return this.httpGet(this.createUrlForQuery({ query: 'getall' }))
      .pipe(map((queryResult: T[]) => {
        const result: T[] = [];
        if ((UtilsMisc.isNullOrUndefined(queryResult) === false) && (queryResult.length > 0)) {
          for (const entity of queryResult) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  protected doGetAllWithPaging$(requestParams: RequestParams = null): Observable<IListResult<T>> {
    let result$: Observable<IListResult<T>> = EMPTY;

    switch (EntityDataService.storageType) {
      case StorageType.Local: {
        result$ = this.doGetAllWithPagingLocal$();
        break;
      }
      case StorageType.Remote: {
        if (UtilsMisc.isNullOrUndefined(requestParams) === true) {
          requestParams = {};
        }
        requestParams.query = 'getallwithpaging';

        result$ = this.doGetAllWithPagingRemote$(requestParams);
        break;
      }
    }

    return result$;
  }

  protected doGetAllWithPagingLocal$(): Observable<IListResult<T>> {
    let result$: Observable<IListResult<T>> = EMPTY;

    if (this.localStorage !== null) {
      result$ = this.localStorage.getAll$().pipe(
        first(),
        map((items: T[]) => {
          return {
            count: items.length,
            items: items,
          }
        })
      );
    }

    return result$;
  }

  protected doGetAllWithPagingRemote$(requestParams: RequestParams): Observable<IListResult<T>> {
    return this.httpGet(this.createUrlForQuery(requestParams))
      .pipe(map((queryResult: any) => {
        const result: IListResult<T> = {
          count: 0,
          items: []
        };
        const items: T[] = [];
        if (queryResult.items === undefined) {
          if (queryResult.length !== undefined) {
            for (const entity of queryResult) {
              items.push(this.createEntityFromJson(entity));
            }
            result.count = queryResult.length;
            result.items = items;
          }
        } else {
          if ((UtilsMisc.isNullOrUndefined(queryResult) === false) && (UtilsMisc.isNullOrUndefined(queryResult.items) === false) &&
            (queryResult.items.length > 0)) {
            for (const entity of queryResult.items) {
              items.push(this.createEntityFromJson(entity));
            }
            result.count = queryResult.count;
            result.items = items;
          }
        }
        return result;
      }));
  }

  protected doGetById$(id: number, storageType?: StorageType): Observable<T> {
    let result$: Observable<T> = EMPTY;

    if (UtilsMisc.isNullOrUndefined(storageType) === true) {
      storageType = EntityDataService.storageType;
    }

    switch (storageType) {
      case StorageType.Local: {
        result$ = this.doGetByIdLocal$(id);
        break;
      }
      case StorageType.Remote: {
        /*if (params === null) {
            params = [];
        }
        params.push(id.toString());*/

        result$ = this.doGetByIdRemote$(id);
        break;
      }
    }

    return result$;
  }

  protected doGetByIdLocal$(id: number): Observable<T> {
    let result$: Observable<T> = EMPTY;

    if (this.localStorage !== null) {
      result$ = this.localStorage.getById$(id);
    }

    return result$;
  }

  protected doGetByIdRemote$(id: number): Observable<T> {
    return this.httpGet(this.createUrlForQuery({ query: 'getbyid', urlParams: [id.toString()] }))
      .pipe(map(entity => {
        if (UtilsMisc.isNullOrUndefinedOrEmpty(entity) === false) {
          return this.createEntityFromJson(entity);
        } else {
          return null;
        }
      }));
  }

  protected doGetByQuery$(requestParams: RequestParams): Observable<IListResult<T>> {
    let result$: Observable<IListResult<T>> = empty();
    if ((UtilsMisc.isNullOrUndefinedOrEmpty(requestParams) === false) && (UtilsMisc.isNullOrUndefinedOrEmpty(requestParams.query) === false)) {
      const transactionId: string = ((UtilsMisc.isNullOrUndefinedOrEmpty(requestParams.transactionId) === false) ? requestParams.transactionId.toString() : null);
      const query: string = requestParams.query.toString().toLowerCase();
      switch (query) {
        /*case 'committransaction': {
            if (UtilsString.stringIsNullOrEmpty(transactionId) === false) {
                result$ = this.transactionService.commitTransaction(this.entityName, params.transactionId.toString()).pipe(map(transaction => {
                    return ([transaction] as any[]);
                }));
            }
            break;
        }
        case 'rollbacktransaction': {
            if (UtilsString.stringIsNullOrEmpty(transactionId) === false) {
                result$ = this.transactionService.rollbackTransaction(this.entityName, params.transactionId.toString()).pipe(map(transaction => {
                    return ([transaction] as any[]);
                }));
            }
            break;
        }
        case 'starttransaction': {
            result$ = this.transactionService.startTransaction(this.entityName).pipe(map(transaction => {
                return ([transaction] as any[]);
            }));
            break;
        }*/
        default: {
          result$ = this.httpGet(this.createUrlForQuery(requestParams), transactionId)
            .pipe(map((queryResult: IListResult<T>) => {
              const result: IListResult<T> = {
                count: 0,
                items: null
              };
              const items: T[] = [];
              if ((UtilsMisc.isNullOrUndefinedOrEmpty(queryResult) === false) && (UtilsMisc.isNullOrUndefined(queryResult.items) === false) &&
                (queryResult.items.length > 0)) {
                for (const entity of queryResult.items) {
                  items.push(this.createEntityFromJson(entity));
                }
                result.count = queryResult.count;
                result.items = items;
              }
              return result;
            }));
          break;
        }
      }
    }

    return result$;
  }

  protected doSave$(entity: T, storageType?: StorageType): Observable<T> {
    let result$: Observable<T> = EMPTY;

    if (UtilsMisc.isNullOrUndefined(storageType) === true) {
      storageType = EntityDataService.storageType;
    }

    switch (storageType) {
      case StorageType.Local: {
        result$ = this.doSaveLocal$(entity);
        break;
      }
      case StorageType.Remote: {
        result$ = this.doSaveRemote$(entity);
        break;
      }
    }

    return result$;
  }

  protected doSaveLocal$(entity: T): Observable<T> {
    let result$: Observable<T> = EMPTY;

    if (this.localStorage !== null) {
      result$ = this.localStorage.save$(entity);
    } else {
      result$ = of(entity);
    }

    return result$;
  }

  protected doSaveRemote$(entity: T): Observable<T> {
    const entityToSave: T = (entity.clone() as T);
    entityToSave.removeReferences();
    if ((entityToSave.id === null) || (entityToSave.id <= 0)) {
      entityToSave.id = null;
    }

    if (entityToSave.id === null) {
      return this.httpPost(this.createUrlForQuery({ query: 'save' }), entityToSave, entityToSave.transactionId)
        .pipe(map(entity => {
          if (UtilsMisc.isNullOrUndefinedOrEmpty(entity) === false) {
            return this.createEntityFromJson(entity);
          } else {
            return null;
          }
        }));
    } else {
      return this.httpPut(this.createUrlForQuery({ query: 'update' }), entityToSave, entityToSave.transactionId)
        .pipe(map(entity => {
          if (UtilsMisc.isNullOrUndefinedOrEmpty(entity) === false) {
            return this.createEntityFromJson(entity);
          } else {
            return null;
          }
        }));
    }
  }

  protected doSaveMulti$(entities: T[]): Observable<boolean> {
    let result$: Observable<boolean> = of(false);

    switch (EntityDataService.storageType) {
      case StorageType.Local: {
        result$ = this.doSaveMultiLocal$(entities);
        break;
      }
      case StorageType.Remote: {
        result$ = this.doSaveMultiRemote$(entities);
        break;
      }
    }

    return result$;
  }

  protected doSaveMultiLocal$(entities: T[]): Observable<boolean> {
    let result$: Observable<boolean> = of(false);

    if (this.localStorage !== null) {
      this.localStorage.saveMulti(entities);
      result$ = of(true);
    }

    return result$;
  }

  protected doSaveMultiRemote$(entities: T[]): Observable<boolean> {
    const result$: Observable<boolean> = of(false);

    const entitiesToSave: T[] = [];
    for (let loop = 0; loop < entities.length; loop++) {
      const entityToSave: T = (entities[loop].clone() as T);
      entityToSave.removeReferences();
      if ((entityToSave.id === null) || (entityToSave.id <= 0)) {
        entityToSave.id = null;
      }
      entitiesToSave.push(entityToSave);
    }

    return this.httpPost(this.createUrlForQuery({ query: 'savemulti' }), entitiesToSave, entitiesToSave[0].transactionId)
      .pipe(map((value: boolean) => {
        return value;
      }));
  }

  protected doGetHttpOptions(transactionId: string = null): any {
  }

  protected doGetUrls(): IUtilsDictionary<string> {
    if (this._urls === null) {
      this._urls = new UtilsDictionary<string>();

      this._urls.add('getall', `${this.apiUrl}`);
      this._urls.add('getallwithpaging', `${this.apiUrl}`);
      this._urls.add('getbyid', `${this.apiUrl}{0}`);
      this._urls.add('delete', `${this.apiUrl}{0}`);
      this._urls.add('save', `${this.apiUrl}`);
      this._urls.add('savemulti', `${this.apiUrl}`);
      this._urls.add('update', `${this.apiUrl}`);
    }

    return this._urls;
  }

  protected createJsonFromEntity(entity: T): T {
    return null;
  }

  protected createEntityFromJson(json: any): T {
    return json;
  }

  protected createUrlForQuery(requestParams: RequestParams = null): string {
    let result: string = this.urls.getValue(requestParams.query);
    let params: string[] = [];
    if (UtilsString.stringIsNullOrEmpty(result) === false) {
      if ((UtilsMisc.isNullOrUndefined(requestParams) === false) && (UtilsMisc.isNullOrUndefined(requestParams.urlParams) === false) && (requestParams.urlParams.length > 0)) {
        params = requestParams.urlParams;
        result = UtilsString.stringFormat(result, params);
      }
      //params.unshift(this.apiUrl);

      if (UtilsMisc.isNullOrUndefined(requestParams.startRow) === false) {
        result = result + requestParams.startRow.toString();
        if (UtilsMisc.isNullOrUndefined(requestParams.rowsToTake) === false) {
          result = result + '/' + requestParams.rowsToTake.toString();
        }
      }
    }

    return result;
  }

  protected getIsConnectedToNetwork(): boolean {
    return true;
  }

  protected handleHttpError(error: Error): Observable<never> {
    let result: Error = error;
    if (error instanceof TimeoutError) {
      result = new ErrorTimeout();
    } else if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = (error as HttpErrorResponse);
      result = new ErrorHttp(httpError.status, httpError.statusText, ((UtilsMisc.isNullOrUndefined(httpError.error) === false) ? httpError.error.errorMessage : httpError.statusText));
      switch (httpError.status) {
        case 0: {
          result.message = 'Es konnte keine Verbindung zum Server aufgebaut werden.';
          break;
        }
        case 403: {
          result.message = 'Keine Berechtigung für Serveraufrufe.';
          break;
        }
      }
    } else {
      result = new ErrorUnknown(error.name, error.message);
    }
    this.loggingService.error(result);
    return throwError(result);
  }

  protected httpDelete(url: string, transactionId: string = null, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (this.getIsConnectedToNetwork() === true) {
      if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
        timeoutInMilliSeconds = this.configurationService.configData.timeout;
      }
      if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
        maxRetries = this.configurationService.configData.retries;
      }
      if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
        retryDelay = this.configurationService.configData.retryDelay;
      }
      return this.http.delete(url, this.doGetHttpOptions(transactionId)).pipe(
        timeout(timeoutInMilliSeconds),
        catchError(this.handleHttpError.bind(this)),
        retryWithDelay(retryDelay, maxRetries)
      );
    } else {
      throw (new ErrorNoNetwork());
    }
  }

  protected httpGet(url: string, transactionId: string = null, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (this.getIsConnectedToNetwork() === true) {
      if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
        timeoutInMilliSeconds = this.configurationService.configData.timeout;
      }
      if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
        maxRetries = this.configurationService.configData.retries;
      }
      if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
        retryDelay = this.configurationService.configData.retryDelay;
      }
      return this.http.get(url, this.doGetHttpOptions(transactionId)).pipe(
        timeout(timeoutInMilliSeconds),
        catchError(this.handleHttpError.bind(this)),
        retryWithDelay(retryDelay, maxRetries)
      );
    } else {
      throw (new ErrorNoNetwork());
    }
  }

  protected httpPost(url: string, body: any, transactionId: string = null, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (this.getIsConnectedToNetwork() === true) {
      if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
        timeoutInMilliSeconds = this.configurationService.configData.timeout;
      }
      if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
        maxRetries = this.configurationService.configData.retries;
      }
      if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
        retryDelay = this.configurationService.configData.retryDelay;
      }
      return this.http.post(url, body, this.doGetHttpOptions(transactionId)).pipe(
        timeout(timeoutInMilliSeconds),
        catchError(this.handleHttpError.bind(this)),
        retryWithDelay(retryDelay, maxRetries)
      );
    } else {
      throw (new ErrorNoNetwork());
    }
  }

  protected httpPut(url: string, body: any, transactionId: string = null, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (this.getIsConnectedToNetwork() === true) {
      if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
        timeoutInMilliSeconds = this.configurationService.configData.timeout;
      }
      if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
        maxRetries = this.configurationService.configData.retries;
      }
      if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
        retryDelay = this.configurationService.configData.retryDelay;
      }
      return this.http.put(url, body, this.doGetHttpOptions(transactionId)).pipe(
        timeout(timeoutInMilliSeconds),
        catchError(this.handleHttpError.bind(this)),
        retryWithDelay(retryDelay, maxRetries)
      );
    } else {
      throw (new ErrorNoNetwork());
    }
  }

}
